import React from 'react';

import { Typography } from 'antd';
import styled from 'styled-components';

import { isMysteryShopAdmin, isMysteryShopShopper } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { useEditShopRequestModalContext } from './EditShopRequestModalContext';
import { USER_GROUPS } from '../../../constants';
import { useShopRequestsContext } from '../ShopRequestsContext';
import ShopRequestStyledSelect from '../ShopRequestStyledSelect';
import ShopRequestUserAvatar from '../ShopRequestUserAvatar';
import ShopRequestUserInfo from '../ShopRequestUserInfo';

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  width: 100%;
`;

const UserLabel = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const Label = styled(Typography.Text)`
  flex-shrink: 0;
  font-family: var(--font-bold);
  white-space: nowrap;
`;

const StyledSelect = styled(ShopRequestStyledSelect)`
  &&&.ant-select {
    margin: 0 !important;
  }
`;

const SelectContainer = styled.div`
  flex-grow: 1;
`;

type Props = {
  field: 'shopper' | 'auditor';
  label: string;
};

const EditShopRequestModalUserSelect = ({ field, label }: Props) => {
  const { shopRequest, setShopRequest } = useEditShopRequestModalContext();
  const { staffUsers } = useShopRequestsContext();
  const { currentUser } = useAppSelector((state) => state.auth);

  const isAdmin = currentUser && isMysteryShopAdmin(currentUser);
  const isSalesperson = currentUser && isMysteryShopShopper(currentUser);

  const currentValue = shopRequest?.[field]?.userId;

  const mysteryShopAdmins = staffUsers?.filter((u) => u.groups?.includes(USER_GROUPS.MYSTERY_SHOP_ADMIN)) ?? [];

  const shoppers =
    staffUsers?.filter((u) => !u.groups?.includes(USER_GROUPS.MYSTERY_SHOP_ADMIN) || u.userId === currentValue) ?? [];

  const list = field === 'auditor' ? mysteryShopAdmins : shoppers;

  const options = [
    ...(currentValue ? [{ value: -1, label: `Unassigned ${label}` }] : []),
    ...list.map((u) => ({ value: u.userId, label: u.name })),
  ];

  const handleChange = (value: number) => {
    let updatedUser = null;

    if (value !== -1) {
      updatedUser = (staffUsers ?? []).find((user) => user.userId === value);
    }

    if (shopRequest) {
      setShopRequest({ ...shopRequest, [field]: updatedUser });
    }
  };

  return (
    <Container>
      <Label>{label}</Label>
      {isAdmin && (
        <SelectContainer>
          <StyledSelect
            width="100%"
            options={options}
            placeholder={`Select ${label}`}
            value={currentValue}
            showSearch
            filterOption={(input, option) =>
              Boolean(option?.label) &&
              typeof option?.label === 'string' &&
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            onChange={handleChange}
            labelRender={({ value, label }) => (
              <UserLabel key={value}>
                <ShopRequestUserAvatar label={label} />
                {label}
              </UserLabel>
            )}
          />
        </SelectContainer>
      )}

      {isSalesperson && shopRequest && (
        <ShopRequestUserInfo shopRequest={shopRequest} field={field} unassignedLabel={`Unassigned ${label}`} />
      )}
    </Container>
  );
};

export default EditShopRequestModalUserSelect;
